import { useState } from 'react';
import { SignInAndUp } from 'supertokens-auth-react/recipe/emailpassword/prebuiltui';
import { SignInAndUp as ThirdPartySignInAndUp } from 'supertokens-auth-react/recipe/thirdparty/prebuiltui';
import Passwordless from 'supertokens-auth-react/recipe/passwordless';

type LoginMethod = 'password' | 'magic-link';

/**
 * Login Page — Clean branded sign-in experience
 */
export function LoginPage() {
  const [loginMethod, setLoginMethod] = useState<LoginMethod>('password');
  const [magicLinkEmail, setMagicLinkEmail] = useState('');
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [magicLinkError, setMagicLinkError] = useState('');
  const [magicLinkLoading, setMagicLinkLoading] = useState(false);

  const handleSendMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!magicLinkEmail.trim()) return;

    setMagicLinkLoading(true);
    setMagicLinkError('');

    try {
      const response = await Passwordless.createCode({ email: magicLinkEmail });
      if (response.status === 'OK') {
        setMagicLinkSent(true);
      } else {
        setMagicLinkError('Failed to send magic link. Please try again.');
      }
    } catch {
      setMagicLinkError('Failed to send magic link. Please try again.');
    } finally {
      setMagicLinkLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Left panel — branding */}
      <div className="hidden lg:flex lg:w-1/2 items-center justify-center bg-gradient-to-br from-indigo-600 to-indigo-800 p-12">
        <div className="max-w-md text-white">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <svg width="24" height="24" viewBox="0 0 32 32" fill="none">
                <path d="M8 10h16M8 16h12M8 22h8" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
                <circle cx="24" cy="22" r="3" fill="white" opacity="0.9"/>
              </svg>
            </div>
            <span className="text-2xl font-bold">Wandool</span>
          </div>
          <h1 className="text-4xl font-bold leading-tight mb-4">
            Project scheduling built for engineers
          </h1>
          <p className="text-lg text-indigo-200 mb-8">
            Full CPM scheduling. Real-time collaboration. AI-powered insights. All in one modern platform.
          </p>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-5 h-5 rounded-full bg-indigo-400/30 flex items-center justify-center">
                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span className="text-indigo-100">All 8 MS Project constraint types</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-5 h-5 rounded-full bg-indigo-400/30 flex items-center justify-center">
                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span className="text-indigo-100">Import from MS Project, CSV, Excel</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-5 h-5 rounded-full bg-indigo-400/30 flex items-center justify-center">
                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span className="text-indigo-100">Free for small teams — no credit card</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right panel — auth form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-8">
            <div className="inline-flex items-center gap-2 mb-2">
              <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
                <svg width="18" height="18" viewBox="0 0 32 32" fill="none">
                  <path d="M8 10h16M8 16h12M8 22h8" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
                  <circle cx="24" cy="22" r="3" fill="white" opacity="0.9"/>
                </svg>
              </div>
              <span className="text-2xl font-bold text-gray-900">Wandool</span>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Welcome back</h2>
              <p className="mt-1 text-sm text-gray-500">Sign in to your account to continue</p>
            </div>

            {/* Google sign-in */}
            <div
              className="[&_[data-supertokens~=container]]:!shadow-none [&_[data-supertokens~=container]]:!border-0 [&_[data-supertokens~=container]]:!p-0 [&_[data-supertokens~=container]]:!m-0 [&_[data-supertokens~=headerTitle]]:!hidden [&_[data-supertokens~=headerSubtitle]]:!hidden [&_[data-supertokens~=divider]]:!hidden [&_[data-supertokens~=superTokensBranding]]:!hidden [&_[data-supertokens~=providerButton]]:!rounded-lg [&_[data-supertokens~=providerButton]]:!border-gray-300 [&_[data-supertokens~=providerButton]]:!py-2.5"
            >
              <ThirdPartySignInAndUp />
            </div>

            {/* Divider */}
            <div className="my-5 flex items-center gap-3">
              <div className="flex-1 border-t border-gray-200" />
              <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">or continue with email</span>
              <div className="flex-1 border-t border-gray-200" />
            </div>

            {/* Method toggle */}
            <div className="flex gap-1 p-1 bg-gray-100 rounded-lg mb-4">
              <button
                type="button"
                className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${
                  loginMethod === 'password'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setLoginMethod('password')}
              >
                Password
              </button>
              <button
                type="button"
                className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${
                  loginMethod === 'magic-link'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => { setLoginMethod('magic-link'); setMagicLinkSent(false); setMagicLinkError(''); }}
              >
                Magic Link
              </button>
            </div>

            {loginMethod === 'password' ? (
              /* Email/password form */
              <div
                className="[&_[data-supertokens~=container]]:!shadow-none [&_[data-supertokens~=container]]:!border-0 [&_[data-supertokens~=container]]:!p-0 [&_[data-supertokens~=container]]:!m-0 [&_[data-supertokens~=headerTitle]]:!hidden [&_[data-supertokens~=headerSubtitle]]:!hidden [&_[data-supertokens~=divider]]:!hidden [&_[data-supertokens~=superTokensBranding]]:!hidden [&_[data-supertokens~=button]]:!rounded-lg [&_[data-supertokens~=button]]:!bg-indigo-600 [&_[data-supertokens~=button]]:!border-indigo-600 [&_[data-supertokens~=button]:hover]:!bg-indigo-700 [&_[data-supertokens~=inputWrapper]]:!rounded-lg [&_[data-supertokens~=inputWrapper]]:!border-gray-300 [&_[data-supertokens~=input]]:!rounded-lg [&_[data-supertokens~=label]]:!font-medium [&_[data-supertokens~=label]]:!text-gray-700 [&_[data-supertokens~=forgotPasswordLink]]:!text-indigo-600"
              >
                <SignInAndUp />
              </div>
            ) : (
              /* Magic link form */
              <div>
                {magicLinkSent ? (
                  <div className="text-center py-4">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">Check your email</h3>
                    <p className="text-sm text-gray-500 mb-4">
                      We sent a magic link to <strong>{magicLinkEmail}</strong>
                    </p>
                    <p className="text-xs text-gray-400 mb-4">
                      Click the link in the email to sign in. The link expires in 15 minutes.
                    </p>
                    <button
                      type="button"
                      className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                      onClick={() => { setMagicLinkSent(false); setMagicLinkEmail(''); }}
                    >
                      Use a different email
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSendMagicLink}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      value={magicLinkEmail}
                      onChange={(e) => setMagicLinkEmail(e.target.value)}
                      placeholder="Enter your email"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent mb-3"
                    />
                    {magicLinkError && (
                      <p className="text-sm text-red-600 mb-3">{magicLinkError}</p>
                    )}
                    <button
                      type="submit"
                      disabled={magicLinkLoading || !magicLinkEmail.trim()}
                      className="w-full py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {magicLinkLoading ? 'Sending...' : 'Send magic link'}
                    </button>
                    <p className="mt-3 text-xs text-center text-gray-400">
                      No password needed — we'll email you a sign-in link
                    </p>
                  </form>
                )}
              </div>
            )}
          </div>

          <p className="mt-6 text-center text-xs text-gray-400">
            By signing in, you agree to our{' '}
            <a href="https://wandool.com/terms" className="text-indigo-600 hover:underline">Terms of Service</a>
            {' '}and{' '}
            <a href="https://wandool.com/privacy" className="text-indigo-600 hover:underline">Privacy Policy</a>
          </p>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
